import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { getCookie } from "../../../lib/helpers";
import { Tag, ReservationRow, PayloadProject } from "../helpers/types";
import { logOutUser } from "../redux/partnerSlice";

// Custom base query to handle 401 errors globally
const baseQueryWithAuth = fetchBaseQuery({
  baseUrl: "/",
  prepareHeaders: (headers, { getState }) => {
    let userToken = getCookie("userToken") || null;
    if (!userToken) {
      throw new Error("Missing user token");
    } else {
      // If we have a token set in state, let's assume that we should be passing it.
      headers.set("Authorization", `Bearer ${userToken}`);
      return headers;
    }
  },
});

// Wrapper function to handle 401 errors globally
const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  let result = await baseQueryWithAuth(args, api, extraOptions);

  // Check for 401 error
  if (result.error && "status" in result.error && result.error.status === 401) {
    // Get current pathname to avoid redirecting if already on login page
    const currentPath = window.location.pathname;
    if (currentPath !== "/login") {
      // Dispatch logout action
      api.dispatch(logOutUser());
      // Redirect to login page
      window.location.href = "/login";
    }
  }

  return result;
};

export const partnerApi = createApi({
  reducerPath: "partnerApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    getLocationsData: builder.query({
      query: (data) => `${data.slug}/locations.json`,
    }),
    getLocationData: builder.query({
      query: (data) => `${data.slug}/locations/${data.id}.json`,
    }),
    getFinancesData: builder.query({
      query: (data) => `${data.slug}/partner_stripe_accounts.json`,
    }),
    getTeamMembersData: builder.query({
      query: (string) => `${string}/team_members.json`,
    }),
    getPermissionsData: builder.query({
      query: (data) => `${data.slug}/permissions.json`,
    }),
    getLocationsDataDisconnected: builder.query({
      query: (string) => `${string}.json?connected_status=disconnected`,
    }),
    getTagsData: builder.query({
      query: () => `/brand_tags.json`,
    }),
    getBrandImagesData: builder.query({
      query: (projectName) => `${projectName}/brand_images.json`,
    }),
    getDashboardData: builder.query({
      query: (projectName) => `${projectName}.json?request_for=all`,
    }),
    getIntegrationsListData: builder.query({
      query: (projectName) => `${projectName}/integrations.json`,
    }),
    getWebsiteListData: builder.query({
      query: (projectName) => `cms_providers.json`,
    }),
    postStripeLink: builder.mutation({
      query: (data) => {
        return {
          url: `${data.projectName}/oauth_links/${data.locationId ? "?location_id=" + data.locationId : "?all_locations=true"}`,
          method: "POST",
        };
      },
    }),
    deleteStripeAccount: builder.mutation({
      query: (data) => {
        const projectName = data.slug;
        const id = Number(data.id);
        return {
          url: `${projectName}/partner_stripe_accounts/${id}`,
          method: "DELETE",
        };
      },
    }),
    postResendTeamMember: builder.mutation({
      query: (data) => {
        let slug = data.team_members.slug;
        delete data.team_members.slug;
        let payload = data;
        return {
          url: `${slug}/team_members.json`,
          method: "POST",
          body: payload,
        };
      },
    }),
    postLogosData: builder.mutation({
      query: (data) => {
        let slug = data.slug;
        let brandId = data.brandId;
        delete data.slug;
        delete data.brandId;
        let payload = data;
        return {
          url: `/${slug}/brands/${brandId}/logos`,
          method: "POST",
          body: payload,
        };
      },
    }),
    putSelectedImagesData: builder.mutation({
      query: (data) => {
        let slug = data.slug;
        let brandId = data.brandId;
        let payload = {
          selected_brand_image_ids: data.setup_media_library,
        };
        return {
          url: `/${slug}/brands/${brandId}/brand_images`,
          method: "PUT",
          body: payload,
        };
      },
    }),
    putEditTeamMember: builder.mutation({
      query: (data) => {
        let slug = data.team_members.slug;
        let id = data.team_members.member_id;
        delete data.team_members.slug;
        delete data.team_members.member_id;
        delete data.team_members.additional_permissions;
        delete data.team_members.partner_project_id;
        let payload = data;
        return {
          url: `${slug}/team_members/${id}`,
          method: "PUT",
          body: payload,
        };
      },
    }),
    deleteTeamMember: builder.mutation({
      query: (data) => {
        let slug = data.team_members.slug;
        let id = data.team_members.member_id;
        delete data.team_members.slug;
        delete data.team_members.member_id;
        let payload = data;
        return {
          url: `${slug}/team_members/${id}`,
          method: "DELETE",
          body: payload,
        };
      },
    }),
    postCheckSnippet: builder.mutation({
      query: (data) => {
        let payload = {
          partner_brand_id: Number(data.id),
        };
        return {
          url: `/${data.project}/popup_js_verifications`,
          method: "POST",
          body: payload,
        };
      },
    }),
    getBrandsData: builder.query({
      query: (data) => `${data.slug}/brands.json`,
    }),
    putStatus: builder.mutation({
      query: (data) => {
        let slug = data.slug;
        let payload = <PayloadProject>{
          project: {},
        };
        if (data.status) {
          payload.project.status = data.status;
        }
        if (data.submit_modal_displayed_at) {
          payload.project.submit_modal_displayed_at =
            data.submit_modal_displayed_at;
        }
        return {
          url: `/${slug}`,
          method: "PUT",
          body: payload,
        };
      },
    }),
    putFinanceData: builder.mutation({
      query: (data) => {
        let slug = data.slug;
        let id = data.id;
        let payload = {
          partner_stripe_account: {
            partner_location_ids: data.partner_location_ids,
          },
        };
        return {
          url: `${slug}/finance/${id}`,
          method: "PUT",
          body: payload,
        };
      },
    }),
    postNeedHelpData: builder.mutation({
      query: (data) => {
        let slug = data.slug;
        let payload = {
          url: data.url,
          step: data.step,
          message: data.message.replace(/\n/g, "<br>"),
        };
        return {
          url: `${slug}/need_help`,
          method: "POST",
          body: payload,
        };
      },
    }),
    putLocationData: builder.mutation({
      query: (data) => {
        let slug = data.slug;
        let id = data.id;
        delete data.slug;
        delete data.id;
        let reservation_phone = null;
        let reservation_link = null;
        let priority_reservation_phone_number = null;
        let priority_reservations_url = null;
        if (data.type !== "integrations") {
          data.res_data = data?.res_data?.filter((res: ReservationRow) => {
            if (res.type === "reservation_phone") {
              reservation_phone = res.contact.replace(/\D/g, "");
            } else if (res.type === "reservation_link") {
              reservation_link = res.contact;
            } else if (res.type === "priority_reservation_phone") {
              priority_reservation_phone_number = res.contact.replace(
                /\D/g,
                "",
              );
            } else if (res.type === "priority_reservations_url") {
              priority_reservations_url = res.contact;
            }
            return res;
          });
        }
        let payload = {
          partner_project_location: {},
        };
        if (data.type === "integrations") {
          payload.partner_project_location = {
            pos_complete: data.pos_complete,
            pos_incomplete_reason: data.pos_incomplete_reason,
            pos_system: data.pos_system,
          };
        } else {
          payload.partner_project_location = {
            name: data.name,
            address_line_one: data.address,
            address_line_two: data.address_2,
            city: data.city,
            state: data.state,
            zip_code: data.zip_code,
            phone: data.phone,
            menu_url: data?.menu_url,
            reservation_phone: reservation_phone,
            reservation_url: reservation_link,
            priority_reservations_url: priority_reservations_url,
            priority_reservation_phone: priority_reservation_phone_number,
            partner_brand_id: data.brand_name,
            steps_data: data?.steps_data,
          };
        }
        return {
          url: `${slug}/locations/${id}`,
          method: "PUT",
          body: payload,
        };
      },
    }),
    putBrandsData: builder.mutation({
      query: (data) => {
        let slug = data.slug;
        let id = data.id;
        let payload = {
          partner_brand: {
            brand_name: data?.brand_name,
            tag_ids: data?.selected_tags.map((tag: Tag) => tag.id),
            favorite_tag_ids:
              data?.favorite_tags?.map((tag: Tag) => tag.id) || [],
            brand_description: data?.description,
            website_url: data?.brand_website,
            gallery_images_ids: data?.gallery_images_ids,
            hero_image_id: data?.hero_image_id,
            logo_dark_id: data?.logo_dark_id,
            logo_light_id: data?.logo_light_id,
            logo_light_wide_id: data?.logo_light_wide_id,
            logo_dark_wide_id: data?.logo_dark_wide_id,
            base_logo_id: data?.base_logo_id,
            no_color_alteration: data?.no_color_alteration,
            steps_data: data?.steps_data,
            popup_incomplete_reason: data?.popup_incomplete_reason,
            popup_complete: data?.popup_complete,
            cms_provider: data?.cms_provider,
          },
        };
        return {
          url: `${slug}/brands/${id}`,
          method: "PUT",
          body: payload,
          formData: true,
        };
      },
    }),
    putFinanceConnection: builder.mutation({
      query: (data) => {
        let id = data.id;
        let slug = data.slug;
        let payload = {
          partner_stripe_account: {
            partner_location_ids: data.checkedLocations,
          },
        };
        return {
          url: `${slug}/finance/${id}.json`,
          method: "PUT",
          body: payload,
          formData: true,
        };
      },
    }),
    putLocationConnection: builder.mutation({
      query: (data) => {
        let id = data.id;
        let slug = data.slug;
        let accountId = data.accountId;
        let payload = {
          partner_project_location: {
            partner_stripe_account_id: accountId,
          },
        };
        return {
          url: `${slug}/locations/${id}`,
          method: "PUT",
          body: payload,
          formData: true,
        };
      },
    }),
    postBrandsImageData: builder.mutation({
      query: (data) => {
        let slug = data.slug;
        let id = data.id;
        delete data.slug;
        delete data.id;
        let payload = {
          partner_brand_image: {
            image: JSON.stringify(data),
          },
        };
        return {
          url: `${slug}/brands/${id}/brand_images`,
          method: "POST",
          body: payload,
          formData: true,
        };
      },
    }),
    deleteBrandLocation: builder.mutation({
      query: (data) => {
        let slug = data.slug;
        let id = data.id;
        let type = data.type;
        return {
          url: `${slug}/${type}/${id}`,
          method: "DELETE",
        };
      },
    }),
    postTeamMember: builder.mutation({
      query: (data) => {
        let slug = data.slug;
        delete data.slug;
        delete data.additional_permissions;
        let payload = {
          team_members: data,
        };
        return {
          url: `${slug}/team_members`,
          method: "POST",
          body: payload,
        };
      },
    }),
  }),
});

export const {
  usePutStatusMutation,
  usePutLocationDataMutation,
  usePostResendTeamMemberMutation,
  usePutEditTeamMemberMutation,
  useDeleteTeamMemberMutation,
  useDeleteBrandLocationMutation,
  usePostCheckSnippetMutation,
  usePutBrandsDataMutation,
  usePutFinanceDataMutation,
  usePostBrandsImageDataMutation,
  usePostStripeLinkMutation,
  useDeleteStripeAccountMutation,
  usePutFinanceConnectionMutation,
  usePutLocationConnectionMutation,
  useLazyGetBrandsDataQuery,
  useLazyGetDashboardDataQuery,
  useLazyGetPermissionsDataQuery,
  usePostTeamMemberMutation,
  usePostLogosDataMutation,
  usePostNeedHelpDataMutation,
  usePutSelectedImagesDataMutation,
} = partnerApi;
